import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";

// Minimal, in-app replacements for window.confirm and window.prompt
const ConfirmContext = createContext({ confirm: async () => true, promptValue: async () => null });

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, kind: "confirm" });
  const resolverRef = useRef(null);
  const [inputValue, setInputValue] = useState("");

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) resolverRef.current(result);
    resolverRef.current = null;
  };

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setState({
      open: true, kind: "confirm",
      title: opts.title || "Are you sure?",
      description: opts.description || "",
      confirmText: opts.confirmText || "Confirm",
      cancelText: opts.cancelText || "Cancel",
      secondaryText: opts.secondaryText || "",
      tone: opts.tone || "default",
    });
  }), []);

  const promptValue = useCallback((opts = {}) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setInputValue(opts.defaultValue || "");
    setState({
      open: true, kind: "prompt",
      title: opts.title || "Enter value",
      description: opts.description || "",
      placeholder: opts.placeholder || "",
      confirmText: opts.confirmText || "Save",
      cancelText: opts.cancelText || "Cancel",
    });
  }), []);

  const onConfirm = () => {
    if (state.kind === "prompt") close(inputValue.trim() ? inputValue : null);
    else close(true);
  };
  const onSecondary = () => close("secondary");
  const onCancel = () => close(state.kind === "prompt" ? null : false);

  return (
    <ConfirmContext.Provider value={{ confirm, promptValue }}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(v) => !v && onCancel()}>
        <AlertDialogContent className="bg-[#111] border-zinc-800 max-w-md" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[16px] tracking-tight">{state.title}</AlertDialogTitle>
            {state.description ? (
              <AlertDialogDescription className="text-[12px] text-zinc-400 whitespace-pre-line leading-relaxed">
                {state.description}
              </AlertDialogDescription>
            ) : (
              <AlertDialogDescription className="sr-only">Confirm or cancel this action.</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {state.kind === "prompt" && (
            <Input value={inputValue} onChange={(e) => setInputValue(e.target.value)}
              placeholder={state.placeholder}
              autoFocus onKeyDown={(e) => { if (e.key === "Enter") onConfirm(); }}
              className="bg-[#0a0a0a] border-zinc-800 text-[13px]" data-testid="confirm-input" />
          )}
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel onClick={onCancel}
              className="bg-transparent border border-zinc-800 hover:bg-zinc-900 text-zinc-300 text-[12px] h-9"
              data-testid="confirm-cancel">{state.cancelText}</AlertDialogCancel>
            {state.kind === "confirm" && state.secondaryText && (
              <button onClick={onSecondary}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white text-[12px] h-9 px-4 rounded-md font-medium"
                data-testid="confirm-secondary">{state.secondaryText}</button>
            )}
            <AlertDialogAction onClick={onConfirm}
              className={`text-[12px] h-9 ${
                state.tone === "destructive"
                  ? "bg-[#EF4444] hover:bg-[#DC2626] text-white"
                  : "bg-white hover:bg-zinc-200 text-black"
              }`}
              data-testid="confirm-ok">{state.confirmText}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() { return useContext(ConfirmContext); }
